import React, { useContext, useRef, useState } from "react";
import { Grid, FormHelperText, Icon } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { AddAddressAPI } from "APIs/DevoteeAPIs";
import { UserContext, notify } from "App";
import FullScreenLoader from "components/Custom/FullScreenLoader";
// Receipt page theme (shared)
import SectionCard, { SectionHeader, ranuButtonSx } from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

// Back button: maroon (theme primary) outline and text, arrow icon on the left
const backButtonSx = ({ palette }) => ({
  color: palette.primary.main,
  borderColor: palette.primary.main,
  backgroundColor: "transparent",
  borderRadius: "10px",
  boxShadow: "none",
  boxSizing: "border-box",
  height: { xs: "2.25rem", md: "2.5rem" },
  minHeight: { xs: "2.25rem", md: "2.5rem" },
  px: 2.5,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  gap: 0.75,
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  // Hover: gold (theme secondary), same as the other outlined buttons
  "&:hover": {
    color: palette.secondary.main,
    borderColor: palette.secondary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "add-devotee-address-form";

// Render order and required flags of the form (same rules as the Edit address screen).
const FIELDS = [
  { name: "title" },
  { name: "phone" },
  { name: "address_line_1", full: true },
  { name: "address_line_2", full: true },
  { name: "landmark" },
  { name: "area" },
  { name: "city", required: true },
  { name: "state" },
  { name: "country" },
  { name: "pincode" },
];

// Format rules, checked only when the field has a value
const VALIDATORS = {
  phone: {
    isValid: (v) => v.length === 10,
    message: "Phone number must be exactly 10 digits",
  },
};

const labelOf = (name) => name.split("_").join(" ");

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// Digits only, max 10 characters
const sanitizePhone = (value) => value.replace(/\D/g, "").slice(0, 10);

const AddDevoteeAddress = () => {
  const { id } = useParams();
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [address, setAddress] = useState({
    devotee_id: id,
    title: "",
    phone: "",
    address_line_1: "",
    address_line_2: "",
    landmark: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
  });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => setAddress((prev) => ({ ...prev, [field]: value }));

  const handleBlur = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  // Real validation state: used to decide whether the form can be saved
  const getError = ({ name, required }) => {
    const value = address[name];

    if (required && String(value).trim() === "") {
      return `${capitalize(labelOf(name))} is required`;
    }

    const rule = VALIDATORS[name];
    if (rule && value !== "" && !rule.isValid(value)) {
      return rule.message;
    }

    return "";
  };

  // What the user sees: an error only appears after the field has been blurred
  const getVisibleError = (field) => (touched[field.name] ? getError(field) : "");

  const isFormValid = FIELDS.every((field) => !getError(field));

  // Scroll to the first field that has an error and focus it
  const scrollToFirstError = () => {
    const firstErrorField = FIELDS.find((field) => getError(field));
    if (!firstErrorField || !formRef.current) return;

    requestAnimationFrame(() => {
      const input = formRef.current.querySelector(`[name="${firstErrorField.name}"]`);
      if (input) {
        input.scrollIntoView({ behavior: "smooth", block: "center" });
        input.focus({ preventScroll: true });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Show every error (if any) when the user tries to save, and scroll to the first one
    if (!isFormValid) {
      const allTouched = {};
      FIELDS.forEach(({ name }) => {
        allTouched[name] = true;
      });
      setTouched(allTouched);
      scrollToFirstError();
      return;
    }

    setLoading(true);
    try {
      const response = await AddAddressAPI(address, userData.access_token, dispatch);
      if (response.is_success) {
        navigate(`/devotee/edit/${id}`, { replace: true });
        notify("Devotee address added!", "success");
      } else {
        notify(response.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      {/* Back button sits right beside the page badge (startAction = left of the badge) */}
      <SectionHeader
        icon="home"
        label="ADD ADDRESS"
        startAction={
          <MDButton
            variant="outlined"
            color="primary"
            sx={backButtonSx}
            onClick={() => navigate(-1)}
          >
            <Icon sx={{ fontSize: "1.1rem !important", fontWeight: "bold" }}>arrow_back</Icon>
            Back
          </MDButton>
        }
      />

      <SectionCard icon="location_on" title="Address" subtitle="Fields marked * are required">
        <form ref={formRef} id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
          <Grid container spacing={2}>
            {FIELDS.map((field) => {
              const { name, required, full } = field;
              const error = getVisibleError(field);

              return (
                <FormField key={name} name={name} required={required} full={full}>
                  <MDInput
                    type={name === "pincode" ? "number" : "text"}
                    name={name}
                    autoComplete="off"
                    value={address[name]}
                    onChange={(e) =>
                      handleChange(
                        name,
                        name === "phone" ? sanitizePhone(e.target.value) : e.target.value
                      )
                    }
                    onBlur={() => handleBlur(name)}
                    error={!!error}
                    placeholder={`Enter ${labelOf(name)}`}
                    fullWidth
                  />
                  {error && <FormHelperText error>{error}</FormHelperText>}
                </FormField>
              );
            })}
          </Grid>
        </form>
      </SectionCard>

      {/* Save button sits outside the form: bottom right on desktop, centered on mobile.
          It still submits the form through the `form` attribute. */}
      <MDBox mt={2} display="flex" justifyContent={{ xs: "center", md: "flex-end" }}>
        <MDButton type="submit" form={FORM_ID} color="primary" sx={ranuButtonSx} disabled={loading}>
          Save Address
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default AddDevoteeAddress;
